import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PipelineAPI } from '../api/PipelineAPI';
import { DEFAULT_NETWORK_REQ_OPTIONS } from '../../request/constants/request-options';
import { toasterService } from '../../toaster/toaster.service';
import { isPromiseCancelError } from '../../shared/utils/promise';

interface UseCreatePipelineProps {
  showSuccessToast?: boolean;
  showErrorToast?: boolean;
  isV1?: boolean;
}

export function useCreatePipeline({
  showSuccessToast = true,
  showErrorToast = true
}: UseCreatePipelineProps) {
  const [creatingPipeline, setCreatingPipeline] = useState(false);
  const { current: abortController } = useRef(new AbortController());

  const navigate = useNavigate();

  useEffect(() => {
    return () => {
      abortController.abort();
    };
  }, []);

  const navigateToCreate = (
    integrationId: number,
    selectedSourceType?: string,
    standardPipelineSeqId?: number
  ) => {
    navigate(
      `/pipeline/edge/${integrationId}/create${
        selectedSourceType ? `?source_type=${selectedSourceType}` : ''
      }${
        standardPipelineSeqId
          ? `${
              selectedSourceType ? '&' : '?'
            }upgrading_pipeline=true&seq_id=${standardPipelineSeqId}`
          : ''
      }`
    );
  };

  const createPipeline = useCallback((selectedSourceType?: string) => {
    setCreatingPipeline(true);

    PipelineAPI.createIntegration({
      uiOptions: { ...DEFAULT_NETWORK_REQ_OPTIONS.uiOptions, showErrorMsg: true },
      networkOptions: {
        signal: abortController.signal
      }
    })
      .then(res => {
        if (showSuccessToast) {
          toasterService.pop('success', `Successfully created pipeline with id ${res.data.id}`);
        }

        navigateToCreate(res.data.id, selectedSourceType);
      })
      .catch(e => {
        if (showErrorToast && !isPromiseCancelError(e)) {
          toasterService.pop('error', `Failed to create new pipeline`, 'Please try again');
        }

        setCreatingPipeline(false);
      });
  }, []);

  const upgradePipeline = useCallback((id: number, seqId: number) => {
    PipelineAPI.upgradePipeline(id)
      .then(res => {
        if (showSuccessToast) {
          toasterService.pop('success', `Successfully upgraded pipeline with id ${res.data.id}`);
        }

        navigateToCreate(res.data.id, undefined, seqId);
      })
      .catch(e => {
        if (showErrorToast && !isPromiseCancelError(e)) {
          toasterService.pop('error', `Failed to upgrade pipeline`, 'Please try again');
        }
      });
  }, []);

  return {
    createPipeline,
    upgradePipeline,
    creatingPipeline
  };
}
